import React, {
	Dispatch,
	SetStateAction,
	useCallback,
	useEffect,
	useRef,
	useState,
} from 'react';
import { Filtros } from '@toctoc/design-system';
import { IEstructuraFiltros } from './Filters.interface';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { setFilter, selectFilter } from '@/app/reducers/filters';
import { filterDataGet } from '@/app/reducers/filtersData';
import { setStatusToIdle } from '@/app/reducers/properties';
import { IFiltros } from '@/components/templates/search/interfaces/IEstructuraPrincipal';
import {
	selectFiltersAvailable,
	setFiltersAvailable,
} from '@/app/reducers/filtersAvailable';
import { constructUrlFromFilters, matchQueryWithValueList, filterVisibleFilters, cleanIncompatibleFilters, filterToUrlSlugMapping } from './util';
import { useRouter } from 'next/router';
import { getUrlParameters } from '@/utils/url';
import { isEmpty } from '@/utils/object';

type FiltersSetterType = Dispatch<SetStateAction<IFiltros[]>>;
// eslint-disable-next-line no-unused-vars
type FiltersSetterFunctionType = (prevState: IFiltros[]) => IFiltros[];

export const Filters = ({
	filtros,
	idsRequeridos,
	isMobile,
	filtrosBase,
	triggerSearch,
	urlSlug,
	pathListaSeo,
	onFiltersReady,
	customBlock,
	customBlockFilterIds,
	forceShallowNavigation = false,
}: IEstructuraFiltros) => {
	const dispatch = useAppDispatch();

	const filtersAvailableData = useAppSelector(selectFiltersAvailable).filtros;
	const filterDataState = useAppSelector((state) => state.FilterDataState);
	const filterState = useAppSelector(selectFilter);
	const router = useRouter();
	//const { pathListaSeo } = router.query;
	const [tof, tpf, region, comuna] = (pathListaSeo as string[]) ?? [];
	const dataAttr = { 'data-apartado': `${tof} ${tpf}` };
	const [filtersSelected, setFiltersSelected] = useState(filterState.filtros);
	const [hardUrlSegmentToFollow, setHardUrlSegmentToFollow] = useState([tof]);

	const setFiltersAvailableData: FiltersSetterType = (prevUpdate) => {
		const newState =
			typeof prevUpdate === 'function'
				? (prevUpdate as FiltersSetterFunctionType)(filtersAvailableData)
				: prevUpdate;
		dispatch(setFiltersAvailable({ filtros: newState }));
	};

	// Enhanced wrapper function that cleans incompatible filters
	const setFiltersSelectedWithCleaning = useCallback((newFiltersOrFunction: React.SetStateAction<IFiltros[]>) => {
		// Handle both direct values and function updates
		setFiltersSelected(currentFilters => {
			const newFilters = typeof newFiltersOrFunction === 'function'
				? newFiltersOrFunction(currentFilters)
				: newFiltersOrFunction;

			// Clean filters that are not compatible according to idsRequeridos
			return cleanIncompatibleFilters(newFilters, idsRequeridos);
		});
	}, [idsRequeridos]);

	// Wrapper function to detect user interactions vs automatic parsing
	const setFiltersSelectedFromUser = useCallback((newFilters: IFiltros[]) => {
		// console.log('👤 [setFiltersSelectedFromUser] User interaction detected');
		userHasInteracted.current = true; // Mark that user has interacted
		const cleanedFilters = cleanIncompatibleFilters(newFilters, idsRequeridos);
		setFiltersSelected(cleanedFilters);
	}, [idsRequeridos]);

	const setFiltersSelectedFromSystem = useCallback((newFilters: IFiltros[]) => {
		// console.log('🤖 [setFiltersSelectedFromSystem] System/automatic change');
		// Don't mark as user interaction
		const cleanedFilters = cleanIncompatibleFilters(newFilters, idsRequeridos);
		setFiltersSelected(cleanedFilters);
	}, [idsRequeridos]);

	const fromRouterPush = useRef(false);
	const useReplaceForNextPush = useRef(false);
	const queryParsed = useRef(false);
	const componentMounted = useRef(false);
	const isFirstLoad = useRef(true);
	const userHasInteracted = useRef(false); // Track real user interactions
	const windowUrlQuery = useRef<{ [key: string]: string | string[] }>({});
	// Tracks the path of the page we're currently on, used by beforePopState to detect
	// back/forward navigation to a different path before window.location updates.
	const currentPathRef = useRef<string>('');

	const setFromPushToFalse = useCallback(() => {
		const [url] = constructUrlFromFilters(filterState.filtros, urlSlug) ?? [];
		const [newParameterLessUrl] = url.split('?');

		const [, filter_tof] = newParameterLessUrl.split('/');
		const [firstSegment] = hardUrlSegmentToFollow;
		if (
			queryParsed.current === true &&
			componentMounted.current &&
			filter_tof !== firstSegment
		)
			fromRouterPush.current = false;
	}, [filterState.filtros, hardUrlSegmentToFollow, urlSlug]);

	// Sincronizar Redux store con filtros de props cuando cambian (shallow navigation)
	useEffect(() => {
		if (filtros && filtros.length > 0) {
			dispatch(setFiltersAvailable({ filtros }))
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [JSON.stringify(filtros)])

	const componentMountEffect = useCallback(() => {
		componentMounted.current = true;
		windowUrlQuery.current = getUrlParameters();
	}, []);

	// Update windowUrlQuery when router query changes
	const routerQueryChangeEffect = useCallback(() => {
		// console.log("🔄 [routerQueryChangeEffect] Starting:", JSON.stringify({
		// 	routerAsPath: router.asPath,
		// 	queryParsed: queryParsed.current,
		// 	componentMounted: componentMounted.current,
		// 	windowUrlQuery: windowUrlQuery.current
		// }, null, 2));

		// Skip if we haven't parsed query yet or component not mounted
		if (queryParsed.current === false || !componentMounted.current) {
			// console.log("⏭️ [routerQueryChangeEffect] Skipping - not parsed or not mounted");
			return;
		}

		// Get current query parameters from URL
		const currentQueryParams = getUrlParameters();
		// console.log("📋 [routerQueryChangeEffect] Current query params:", JSON.stringify(currentQueryParams, null, 2));

		// Exclude non-filter params from comparison — these are UI state managed outside the filter system
		const filterParamsOnly = (params: { [key: string]: string | string[] }) => {
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { pagina: _pagina, ...rest } = params;
			delete (rest as Record<string, unknown>).view;
			delete (rest as Record<string, unknown>).viewboard;
			delete (rest as Record<string, unknown>).zoom;
			return rest;
		};

		// Check if filter-related query parameters have changed (ignoring pagina)
		const queryParamsChanged =
			JSON.stringify(filterParamsOnly(windowUrlQuery.current)) !==
			JSON.stringify(filterParamsOnly(currentQueryParams));

		if (!queryParamsChanged) {
			// console.log("✅ [routerQueryChangeEffect] Query params unchanged");
			windowUrlQuery.current = currentQueryParams;
			return;
		}

		// console.log("🔄 [routerQueryChangeEffect] Query params changed:", JSON.stringify({
		// 	before: windowUrlQuery.current,
		// 	after: currentQueryParams
		// }, null, 2));

		// Update the stored query params
		windowUrlQuery.current = currentQueryParams;

		// Process query parameters and convert to filters (excluding pagina)
		const paramsForFilters = filterParamsOnly(currentQueryParams);
		const queryFilters: IFiltros[] = [];

		if (Object.keys(paramsForFilters).length > 0) {
			// console.log("🔍 [routerQueryChangeEffect] Processing query params:", JSON.stringify(currentQueryParams, null, 2));

			const matchedFilters = matchQueryWithValueList(
				paramsForFilters,
				filtersAvailableData,
			);

			// console.log("✨ [routerQueryChangeEffect] Matched filters:", JSON.stringify(matchedFilters, null, 2));

			// Force new references to ensure React detects changes
			matchedFilters.forEach((filter) => {
				queryFilters.push(JSON.parse(JSON.stringify(filter)));
			});
		}

		// Detect filters that were query params but are no longer in the URL
		const queryFilterIds = new Set(queryFilters.map(f => f.id));
		const filtersToRemove = filterState.filtros.filter(f =>
			f.id !== undefined &&
			filterToUrlSlugMapping[f.id] === undefined && // not a URL-segment filter
			!queryFilterIds.has(f.id) &&                  // not being replaced by a new value
			paramsForFilters[f.id] === undefined           // not present in current query string
		);

		if (queryFilters.length > 0 || filtersToRemove.length > 0) {
			// console.log("🎯 [routerQueryChangeEffect] Applying query filters:", queryFilters.map(f => ({ id: f.id, values: f.values })));
			// console.log("🗑️ [routerQueryChangeEffect] Removing filters:", filtersToRemove.map(f => f.id));

			let newFiltersSelected = [...filterState.filtros];

			// Remove filters that are no longer in the URL
			filtersToRemove.forEach(f => {
				newFiltersSelected = newFiltersSelected.filter(sf => sf.id !== f.id);
			});

			// Add/update filters present in the URL
			queryFilters.forEach((queryFilter) => {
				newFiltersSelected = newFiltersSelected.filter(
					(f) => f.id !== queryFilter.id,
				);
				newFiltersSelected.push(queryFilter);
			});

			// console.log("🔄 [routerQueryChangeEffect] Setting new filters:", newFiltersSelected.map(f => ({ id: f.id, values: f.values })));
			setFiltersSelectedFromSystem(newFiltersSelected);
		}
	}, [filtersAvailableData, filterState.filtros, setFiltersSelectedFromSystem]);

	const hardUrlSegmentsToFollowChangesEffet = useCallback(() => {
		if (componentMounted.current === false) return;
		setHardUrlSegmentToFollow([tof]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tof]);

	const queryParsedEffect = useCallback(() => {
		const urlQuery = windowUrlQuery.current;
		const componentIsNotMountedYet = componentMounted.current === false;

		if (
			queryParsed.current === true ||
			isEmpty(urlQuery) ||
			filtersAvailableData === undefined ||
			filtersAvailableData.length === 0 ||
			componentIsNotMountedYet
		) {
			if (isEmpty(urlQuery) && queryParsed.current === false) {
				queryParsed.current = true;
				setFromPushToFalse();
				// Change status from init to idle when no query parameters
				dispatch(setStatusToIdle());
			}
			return;
		}
		const matchedValues = matchQueryWithValueList(
			urlQuery,
			filtersAvailableData,
		);

		// Set queryParsed immediately - no need for timeout with semantic URL checking
		queryParsed.current = true;
		if (
			matchedValues === undefined ||
			matchedValues === null ||
			matchedValues.length === 0
		)
			return setFromPushToFalse;
		const removeDupped = matchedValues.filter(
			(m) => filterState.filtros.find((f) => f.id === m.id) === undefined,
		);

		if (removeDupped.length === 0) return setFromPushToFalse();
		setFiltersSelectedFromSystem([
			...filterState.filtros,
			...(removeDupped as IFiltros[]),
		]);
		return setFromPushToFalse();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filtersAvailableData, setFromPushToFalse, setFiltersSelectedFromSystem]);

	const urlUpdateByFilterChangesEffect = useCallback(() => {
		// console.log(
		// 	'🔄 [urlUpdateByFilterChangesEffect] Starting:',
		// 	JSON.stringify(
		// 		{
		// 			filtersSelected: filtersSelected.map((f) => ({
		// 				id: f.id,
		// 				values: f.values,
		// 			})),
		// 			fromRouterPush: fromRouterPush.current,
		// 			componentMounted: componentMounted.current,
		// 			routerAsPath: router.asPath,
		// 		},
		// 		null,
		// 		2,
		// 	),
		// );

		// Only block if we're in the middle of a router push operation
		if (fromRouterPush.current === true) {
			// console.log(
			// 	'⏭️ [urlUpdateByFilterChangesEffect] Skipping - fromRouterPush',
			// );
			return;
		}

		// Don't block on queryParsed = false if component is mounted - user interactions should work
		if (!componentMounted.current) {
			// console.log('⏭️ [urlUpdateByFilterChangesEffect] Skipping - not mounted');
			return;
		}

		if (queryParsed.current === true && componentMounted.current)
			fromRouterPush.current = false;

		const [url, params_formated] =
			constructUrlFromFilters(filtersSelected, urlSlug) ?? [];

		if (url === undefined) {
			// console.warn('Unable to extract url from filters');
			return;
		}

		// console.log(
		// 	'🆕 [urlUpdateByFilterChangesEffect] Constructed URL:',
		// 	JSON.stringify(
		// 		{
		// 			url,
		// 			params_formated,
		// 			currentPath: router.asPath,
		// 		},
		// 		null,
		// 		2,
		// 	),
		// );

		const [newParameterLessUrl] = url.split('?');
		const hasTrailingSlash =
			newParameterLessUrl.lastIndexOf('/') === newParameterLessUrl.length - 1;
		const currentUrl =
			'/' +
			(pathListaSeo as string[]).join('/') +
			(hasTrailingSlash ? '/' : '');

		// Skip if is the same path (and has no parameters)
		if (
			currentUrl === '/' &&
			params_formated === '' &&
			currentUrl === newParameterLessUrl
		) {
			// console.log("✅ [urlUpdateByFilterChangesEffect] URLs match, no push needed");
			return;
		}

		// Compare directly with router.asPath - simpler and more reliable
		const currentRouterUrl = router.asPath;
		const targetUrl = url;

		// Strip UI-state params before comparing: these are managed outside the filter system
		// and should not trigger a filter URL push on their own.
		// NOTE: do NOT use URLSearchParams.toString() here — it re-encodes commas as %2C,
		// which mismatches constructFlexibleUrl output that uses raw commas (e.g. precios=0,4000)
		const stripNonFilterParams = (u: string) => {
			const [path, search] = u.split('?');
			if (!search) return u;
			const newSearch = search
				.split('&')
				.filter(part => !part.startsWith('pagina=') && !part.startsWith('view=') && !part.startsWith('viewboard=') && !part.startsWith('zoom=') && !part.startsWith('b=') && !part.startsWith('debug='))
				.join('&');
			return newSearch ? `${path}?${newSearch}` : path;
		};

		// Skip if the URLs are identical (ignoring non-filter params)
		if (currentRouterUrl === targetUrl || stripNonFilterParams(currentRouterUrl) === stripNonFilterParams(targetUrl)) {
			// console.log(
			// 	'✅ [urlUpdateByFilterChangesEffect] URLs identical, no push needed',
			// );
			return;
		}

		// Skip URL changes ONLY until user has actually interacted with filters
		// This prevents changing the URL from tracking parameters like ?o= on initial load
		// but allows URL changes once user starts interacting with filters
		if (isFirstLoad.current === true && !userHasInteracted.current) {
			// console.log(
			// 	'✅ [urlUpdateByFilterChangesEffect] No user interaction yet, maintaining original URL',
			// );
			return; // Don't update URL until user interacts
		}

		// Mark first load as complete once user has interacted
		if (userHasInteracted.current && isFirstLoad.current === true) {
			// console.log(
			// 	'🎯 [urlUpdateByFilterChangesEffect] User has interacted, marking first load complete',
			// );
			isFirstLoad.current = false;
		}

		// console.log(
		// 	'🚀 [urlUpdateByFilterChangesEffect] URL differs, pushing:',
		// 	JSON.stringify(
		// 		{
		// 			from: router.asPath,
		// 			to: url,
		// 		},
		// 		null,
		// 		2,
		// 	),
		// );

		// En v1: shallow solo si el PATH es idéntico (query params solo).
		// Si el path cambia, Next.js debe re-correr getStaticProps para consultar MongoDB.
		// En v2 (forceShallowNavigation): siempre shallow — los filtros se resuelven client-side.
		const canBeShallow = newParameterLessUrl === currentUrl || forceShallowNavigation;

		// ALWAYS set fromRouterPush to prevent infinite loops
		fromRouterPush.current = true;

		if (canBeShallow === false) {
			// componentMounted.current = false;
			queryParsed.current = false;
		}
		if (canBeShallow === false && triggerSearch.current !== undefined) {
			clearTimeout(triggerSearch.current);
		}

		// Preservar view/viewboard/zoom cuando es shallow (misma página o v2).
		const uiParams = canBeShallow
			? ['view', 'viewboard', 'zoom', 'b', 'debug']
				.filter(k => router.query[k])
				.map(k => `${k}=${router.query[k]}`)
				.join('&')
			: '';
		const urlWithUiParams = uiParams
			? url + (url.includes('?') ? `&${uiParams}` : `?${uiParams}`)
			: url;

		const shouldReplace = useReplaceForNextPush.current;
		useReplaceForNextPush.current = false;
		const navPromise = shouldReplace
			? router.replace(urlWithUiParams, undefined, { shallow: canBeShallow })
			: router.push(urlWithUiParams, undefined, { shallow: canBeShallow });
		navPromise.then(() => {
			// Update windowUrlQuery immediately after successful navigation
			windowUrlQuery.current = getUrlParameters();
			// Reset the flag immediately - semantic checking prevents infinite loops
			fromRouterPush.current = false;
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [filtersSelected, urlSlug, router.asPath]);

	const filterSelectedChangesEffect = useCallback(() => {
		// console.log('🔄 [filterSelectedChangesEffect] Filters changed:', 
		// 	filtersSelected.map(f => ({ id: f.id, values: f.values }))
		// );
		dispatch(setFilter({ filtros: filtersSelected }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dispatch, filtersSelected]);

	// Detect user interactions with filters after initial load
	const userInteractionDetectionEffect = useCallback(() => {
		// console.log("🔍 [userInteractionDetectionEffect] Called:", JSON.stringify({
		// 	componentMounted: componentMounted.current,
		// 	queryParsed: queryParsed.current,
		// 	isFirstLoad: isFirstLoad.current,
		// 	userHasInteracted: userHasInteracted.current,
		// 	filtersCount: filtersSelected.length
		// }, null, 2));

		// Only start detecting after component is mounted and query is parsed
		if (!componentMounted.current || !queryParsed.current) {
			// console.log("⏭️ [userInteractionDetectionEffect] Skipping - not ready");
			return;
		}

		// If we're still in first load state and filters have changed, it means user interacted
		if (isFirstLoad.current === true && !userHasInteracted.current) {
			// console.log("⚠️ [userInteractionDetectionEffect] POTENTIALLY PROBLEMATIC - Setting timeout for user interaction detection");
			// Small delay to distinguish between automatic parsing and user interaction
			const timeoutId = setTimeout(() => {
				if (isFirstLoad.current === true) {
					// console.log("🎯 [userInteractionDetectionEffect] TIMEOUT FIRED - Marking as user interaction (this might cause duplicate search)");
					userHasInteracted.current = true;
				}
			}, 100); // Very short delay to catch user interactions

			return () => {
				// console.log("🧹 [userInteractionDetectionEffect] Cleaning up timeout");
				clearTimeout(timeoutId);
			};
		}
	}, [filtersSelected]);

	const removeFilter = useCallback((filterId: string) => {
		const newFilters = filtersSelected.filter(f => f.id !== filterId);
		setFiltersSelectedFromUser(newFilters);
	}, [filtersSelected, setFiltersSelectedFromUser]);

	const removeFilterReplace = useCallback((filterId: string) => {
		useReplaceForNextPush.current = true;
		const newFilters = filtersSelected.filter(f => f.id !== filterId);
		setFiltersSelectedFromUser(newFilters);
	}, [filtersSelected, setFiltersSelectedFromUser]);

	const updateFilter = useCallback((filterId: string, newFilter: IFiltros | null) => {
		const withoutFilter = filtersSelected.filter(f => f.id !== filterId);
		setFiltersSelectedFromUser(newFilter ? [...withoutFilter, newFilter] : withoutFilter);
	}, [filtersSelected, setFiltersSelectedFromUser]);

	useEffect(() => {
		onFiltersReady?.({ removeFilter, removeFilterReplace, updateFilter });
	}, [removeFilter, removeFilterReplace, updateFilter, onFiltersReady]);

	// When browser back/forward navigates to a different path, force a full reload.
	// This ensures getStaticProps re-runs and Redux filter state is re-initialized from SSR,
	// preventing urlUpdateByFilterChangesEffect from redirecting forward with stale filter state.
	// Keep currentPathRef in sync with the current page path (before any popstate fires).
	// beforePopState fires AFTER the browser URL changed, so window.location.pathname
	// is already the new (back) path — we need to compare against the stored previous path.
	useEffect(() => {
		currentPathRef.current = router.asPath.split('?')[0].replace(/\/$/, '');
	}, [router.asPath]);

	// When browser back/forward navigates to a different path, force a full reload.
	// This ensures getStaticProps re-runs and Redux filter state is re-initialized from SSR,
	// preventing urlUpdateByFilterChangesEffect from redirecting forward with stale filter state.
	// NOTE: use `as` (the display URL), not `url` (Next.js internal route pattern).
	useEffect(() => {
		router.beforePopState(({ as }) => {
			const targetPath = as.split('?')[0].replace(/\/$/, '');
			const storedCurrentPath = currentPathRef.current;
			if (storedCurrentPath && targetPath !== storedCurrentPath) {
				window.location.href = as;
				return false;
			}
			return true;
		});
		return () => {
			router.beforePopState(() => true);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(componentMountEffect, [componentMountEffect]);
	useEffect(hardUrlSegmentsToFollowChangesEffet, [
		hardUrlSegmentsToFollowChangesEffet,
	]);
	useEffect(queryParsedEffect, [queryParsedEffect]);
	useEffect(urlUpdateByFilterChangesEffect, [urlUpdateByFilterChangesEffect]);
	useEffect(filterSelectedChangesEffect, [filterSelectedChangesEffect]);
	useEffect(routerQueryChangeEffect, [routerQueryChangeEffect, router.asPath]);
	useEffect(userInteractionDetectionEffect, [userInteractionDetectionEffect]);

	// Filter visible filters based on dependencies
	const visibleFilters = filterVisibleFilters(filtersAvailableData, filtersSelected) as IFiltros[];

	// Ensure dependent filters that are loading data have at least a placeholder value
	// so they render in the UI while data is being fetched
	const filtrosWithPlaceholders = visibleFilters.map(filter => {
		// Check if this is a dynamically-loaded filter (like comuna when region changes)
		if ((filter.id === 'comuna' || filter.id === 'barrio') && (!filter.values || filter.values.length === 0)) {
			// Check if the parent filter (region) has a value
			const regionFilter = filtersSelected.find(f => f.id === 'region');
			if (regionFilter && regionFilter.values && regionFilter.values.length > 0) {
				// Add a placeholder value so the component renders
				return {
					...filter,
					values: [{
						id: -1,
						label: 'Cargando...',
						value: []
					}]
				};
			}
		}
		return filter;
	});

	// Replace specified filters with customBlock placeholders (visual only — original defs still drive state)
	const filtrosForRender = customBlockFilterIds?.length
		? filtrosWithPlaceholders.map(f =>
			customBlockFilterIds.includes(f.id ?? '')
				? { ...f, type: 'customBlock', name: '' } as IFiltros
				: f
		)
		: filtrosWithPlaceholders;
	const routerPath = router.asPath;
	const pathList = routerPath.startsWith("/") ? routerPath.slice(1) : routerPath;

	return (
		<Filtros
			filtros={filtrosForRender}
			setFiltros={setFiltersAvailableData as any}
			useFilterDependency={() => [
				filterDataState,
				(filterId, value) =>
					new Promise((res) => {
						dispatch(filterDataGet({ filterId, value, pathList: pathList }));
						res(undefined);
					}),
			]}
			isMobile={isMobile}
			filtersSelected={filtersSelected}
			setFiltersSelected={setFiltersSelectedWithCleaning}
			idsRequeridos={idsRequeridos}
			filtrosBase={filtrosBase}
			customBlock={customBlock}
			{...dataAttr}
		/>
	);
};
